import type {
  BreakSheetPaper,
  BreakSheetRun,
  FieldBreakSheetRequest,
  ListBreakSheetRunsRequest,
  OpenBreakSheetRunRequest,
  PlainBreakSheetRequest,
} from '../shared/desktop-api';
import { localePaper } from '../renderer/break-sheet-paper';
import type { UploadChannel } from './upload';

/** The API's `BreakSheetRun` (services/Paige.Contracts/openapi/paige-v1.yaml). */
interface ApiRun {
  id: string;
  kind: 'plain' | 'fields';
  sheetCount: number;
  paper: BreakSheetPaper;
  createdAt: string;
  pdf: string;
  sheets: { id: string }[];
}

/**
 * Print break sheets, in the browser (docs/runbooks/break-sheets.md). The desktop wrote a PDF to the
 * workstation's disk and opened it with the computer's PDF handler; Paige records the run through
 * `POST /v1/jobs/{id}/break-sheets` (each sheet's values stored before any paper exists) and the
 * browser downloads the PDF the API draws, which the person prints at actual size. A run is the
 * job's, so every call goes to the job's own channel.
 */
export function breakSheetCommands(
  channelFor: (workspaceJobId: string) => UploadChannel,
  mount: UploadChannel,
): Record<string, unknown> {
  const runs = new Map<string, { channel: UploadChannel; run: ApiRun }>();
  const remember = (channel: UploadChannel, run: ApiRun): BreakSheetRun => {
    runs.set(run.id, { channel, run });
    return toRun(run);
  };
  return {
    createPlainBreakSheets: async (request: PlainBreakSheetRequest) => {
      const channel = request.jobId ? channelFor(request.jobId) : mount;
      const body = { kind: 'plain', count: request.count, paper: request.paper ?? defaultPaper() };
      return remember(channel, await send<ApiRun>(channel, '/break-sheets', body));
    },
    createFieldBreakSheets: async (request: FieldBreakSheetRequest) => {
      const channel = channelFor(request.jobId);
      const body = {
        kind: 'fields',
        paper: request.paper ?? defaultPaper(),
        sheets: request.sheets,
      };
      return remember(channel, await send<ApiRun>(channel, '/break-sheets', body));
    },
    listBreakSheetRuns: async (request: ListBreakSheetRunsRequest) => {
      const channel = channelFor(request.jobId);
      const listed = await send<ApiRun[]>(channel, '/break-sheets');
      return listed.map((run) => remember(channel, run));
    },
    openBreakSheetRun: async (request: OpenBreakSheetRunRequest) => {
      const known = runs.get(request.runId);
      if (!known) throw new Error('That print is no longer listed here. Open break sheets again.');
      await downloadPdf(known.channel, known.run);
    },
  };
}

const defaultPaper = (): BreakSheetPaper => localePaper(navigator.language);

function toRun(run: ApiRun): BreakSheetRun {
  return {
    runId: run.id,
    jobId: null,
    kind: run.kind,
    sheetCount: run.sheetCount,
    sheetIds: run.sheets.map((sheet) => sheet.id),
    createdAt: run.createdAt,
    paper: run.paper,
  };
}

function headers(channel: UploadChannel): Record<string, string> {
  return channel.tenant ? { 'Paige-Tenant': channel.tenant } : {};
}

async function send<T>(channel: UploadChannel, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${channel.engineOrigin}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    credentials: 'include',
    headers: {
      ...headers(channel),
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok)
    throw new Error(await problemOf(response, 'The break sheets could not be created.'));
  return (await response.json()) as T;
}

/** The API answers with its root path (`/v1/jobs/...`); the channel's origin is the API's `/v1` mount. */
async function downloadPdf(channel: UploadChannel, run: ApiRun): Promise<void> {
  const apiRoot = channel.engineOrigin.replace(/\/v1(\/.*)?$/, '');
  const response = await fetch(`${apiRoot}${run.pdf}`, {
    credentials: 'include',
    headers: headers(channel),
  });
  if (!response.ok)
    throw new Error(await problemOf(response, 'The sheets could not be downloaded.'));
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = `paige-break-sheets-${run.createdAt.slice(0, 10)}-${run.paper}.pdf`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

async function problemOf(response: Response, fallback: string): Promise<string> {
  try {
    const problem = (await response.json()) as { detail?: string; title?: string };
    return problem.detail ?? problem.title ?? fallback;
  } catch {
    return fallback;
  }
}
